<template>
  <div class="stripe-invoices-table">
    <!-- Header with filters -->
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold text-gray-900">
        Invoices
      </h3>
      <div class="flex items-center gap-2">
        <select
          v-model="filters.status"
          class="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          @change="fetchInvoices">
          <option value="">
            All Status
          </option>
          <option value="paid">
            Paid
          </option>
          <option value="open">
            Open
          </option>
          <option value="draft">
            Draft
          </option>
          <option value="void">
            Void
          </option>
          <option value="uncollectible">
            Uncollectible
          </option>
        </select>
        <button
          class="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
          @click="fetchInvoices">
          <i class="fas fa-sync-alt" />
          Refresh
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div
      v-if="loading"
      class="flex items-center justify-center py-8">
      <i class="fas fa-spinner fa-spin text-2xl text-indigo-600" />
    </div>

    <!-- Invoices table -->
    <div
      v-else-if="invoices.length > 0"
      class="overflow-x-auto border border-gray-200 rounded-lg">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Invoice
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Paid
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Due
            </th>
            <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr
            v-for="invoice in invoices"
            :key="invoice.id"
            class="hover:bg-gray-50">
            <td class="px-4 py-3 whitespace-nowrap text-sm">
              <div>
                <div class="font-medium text-gray-900">
                  {{ invoice.number || invoice.stripeInvoiceId }}
                </div>
                <div
                  v-if="invoice.description"
                  class="text-xs text-gray-500">
                  {{ invoice.description }}
                </div>
              </div>
            </td>
            <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
              {{ formatDate(invoice.createdAt) }}
            </td>
            <td class="px-4 py-3 whitespace-nowrap">
              <span
                :class="getStatusClass(invoice.status, invoice.overdue)"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                {{ getStatusText(invoice.status, invoice.overdue) }}
              </span>
            </td>
            <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
              ${{ formatCurrency(invoice.total / 100) }}
            </td>
            <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
              ${{ formatCurrency(invoice.amountPaid / 100) }}
            </td>
            <td class="px-4 py-3 whitespace-nowrap text-sm text-right">
              <div>
                <div class="text-gray-900">
                  ${{ formatCurrency(invoice.amountRemaining / 100) }}
                </div>
                <div
                  v-if="invoice.dueDate"
                  class="text-xs text-gray-500">
                  Due {{ formatDate(invoice.dueDate) }}
                </div>
              </div>
            </td>
            <td class="px-4 py-3 whitespace-nowrap text-center text-sm">
              <div class="flex items-center justify-center gap-2">
                <a
                  v-if="invoice.hostedInvoiceUrl"
                  :href="invoice.hostedInvoiceUrl"
                  target="_blank"
                  class="text-indigo-600 hover:text-indigo-900"
                  title="View Invoice">
                  <i class="fas fa-external-link-alt" />
                </a>
                <a
                  v-if="invoice.invoicePdf"
                  :href="invoice.invoicePdf"
                  target="_blank"
                  class="text-indigo-600 hover:text-indigo-900"
                  title="Download PDF">
                  <i class="fas fa-file-pdf" />
                </a>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="text-center py-8 text-gray-500">
      <i class="fas fa-file-invoice text-4xl mb-3 text-gray-300" />
      <p>No invoices found</p>
    </div>

    <!-- Pagination -->
    <div
      v-if="pagination.totalPages > 1"
      class="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
      <div class="flex-1 flex justify-between sm:hidden">
        <button
          :disabled="pagination.currentPage === 1"
          class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md
                 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          @click="changePage(pagination.currentPage - 1)">
          Previous
        </button>
        <button
          :disabled="pagination.currentPage === pagination.totalPages"
          class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md
                 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          @click="changePage(pagination.currentPage + 1)">
          Next
        </button>
      </div>
      <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700">
            Showing
            <span class="font-medium">{{ (pagination.currentPage - 1) * pagination.perPage + 1 }}</span>
            to
            <span class="font-medium">{{ Math.min(pagination.currentPage * pagination.perPage, pagination.totalCount) }}</span>
            of
            <span class="font-medium">{{ pagination.totalCount }}</span>
            results
          </p>
        </div>
        <div>
          <nav
            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
            aria-label="Pagination">
            <button
              :disabled="pagination.currentPage === 1"
              class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white
                     text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="changePage(pagination.currentPage - 1)">
              <span class="sr-only">Previous</span>
              <i class="fas fa-chevron-left" />
            </button>
            <button
              :disabled="pagination.currentPage === pagination.totalPages"
              class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white
                     text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="changePage(pagination.currentPage + 1)">
              <span class="sr-only">Next</span>
              <i class="fas fa-chevron-right" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from '@/init/axios'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

const loading = ref(false)
const invoices = ref([])
const filters = ref({
  status: '',
  paid: ''
})
const pagination = ref({
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  perPage: 50
})

const fetchInvoices = async(page = 1) => {
  loading.value = true
  try {
    const params = {
      page,
      per_page: pagination.value.perPage
    }

    if (filters.value.status) {
      params.status = filters.value.status
    }

    const response = await axios.get(`/ajax/organizations/${props.organizationId}/stripe_invoices`, { params })
    invoices.value = response.data.data
    pagination.value = response.data.pagination
  } catch (error) {
    // Handle error silently
  } finally {
    loading.value = false
  }
}

const changePage = (page) => {
  if (page >= 1 && page <= pagination.value.totalPages) {
    fetchInvoices(page)
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatCurrency = (amount) => amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const getStatusClass = (status, overdue) => {
  if (overdue) return 'bg-red-100 text-red-800'

  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800'
    case 'open':
      return 'bg-yellow-100 text-yellow-800'
    case 'draft':
      return 'bg-gray-100 text-gray-800'
    case 'void':
      return 'bg-gray-100 text-gray-800'
    case 'uncollectible':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getStatusText = (status, overdue) => {
  if (overdue) return 'Overdue'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

onMounted(() => {
  fetchInvoices()
})
</script>

<style scoped>
/* Add any custom styles here */
</style>
